import { React, useEffect, useState } from 'react';

import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/reimbursement.css';

const DEFAULT_CATEGORY = '2.04.83';

const reimbursementCategories = [
    { value: DEFAULT_CATEGORY, label: 'UC - COMPENSAÇÃO' },
    { value: '2.04.85', label: 'REEMBOLSO' }
];

const hasNumericValue = (value) => value !== '' && value !== null && !Number.isNaN(Number(value));

const LancarReembolso = () => {
    const [projeto, setProjeto] = useState('');
    const [projetos, setProjetos] = useState([]);
    const [valor, setValor] = useState('');
    const [dataVencimento, setDataVencimento] = useState(null);
    const [errorVencimento, setErrorVencimento] = useState(false);
    const [chavePix, setChavePix] = useState('');
    const [categoria, setCategoria] = useState(DEFAULT_CATEGORY);
    const [notaFiscal, setNotaFiscal] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    useEffect(() => {
        async function getProjetos() {
            const response = await fetchFlask('getProjeto');
            const listaProjetos = Array.isArray(response?.dados) ? response.dados : [];
            setProjetos(listaProjetos);
        }

        getProjetos();
    }, []);

    async function submitLancarReembolso(e) {
        e.preventDefault();
        setIsSubmit(true);

        let vencimento = '';
        if (dataVencimento) {
            setErrorVencimento(false);
            vencimento = formatIsoDate(dataVencimento);
        } else {
            setErrorVencimento(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            projeto,
            valor,
            vencimento,
            chave_pix: chavePix,
            nota_fiscal: notaFiscal,
            categoria
        });

        try {
            const response = await fetchFlask('lancarReembolso', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setCategoria(DEFAULT_CATEGORY);
                setProjeto('');
                setValor('');
                setDataVencimento(null);
                setErrorVencimento(false);
                setChavePix('');
                setNotaFiscal('');
            }
        } finally {
            setIsSubmit(false);
        }
    }

    function getProjetoValue(item) {
        if (typeof item !== 'object' || item === null) {
            return item;
        }

        return item.Codigo || item.id || item.codigo || item.Nome || item.nome || item.projeto || '';
    }

    function getProjetoLabel(item) {
        if (typeof item !== 'object' || item === null) {
            return item;
        }

        return item.Nome || item.nome || item.descricao || item.projeto || item.Codigo || item.id || item.codigo || '';
    }

    const selectedCategory = reimbursementCategories.find((item) => item.value === categoria);
    const selectedProject = projetos.find((item) => getProjetoValue(item) === projeto);
    const selectedProjectLabel = selectedProject ? getProjetoLabel(selectedProject) : '';
    const valueLabel = hasNumericValue(valor) ? formatCurrency(Number(valor)) : 'Não informado';
    const dueDateLabel = formatDateLabelPtBr(dataVencimento);
    const completedFields = [
        categoria,
        projeto,
        hasNumericValue(valor),
        notaFiscal,
        dataVencimento,
        chavePix
    ].filter(Boolean).length;

    return (
        <Box className='mainBox ui-workflow-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Lançar Reembolso
            </PageHeader>

            <form action='' method='POST' id='formLancarReembolso' onSubmit={submitLancarReembolso} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Reembolso com conferência de projeto, valor e PIX</h2>
                        </Box>
                        <p>
                            Classifique o lançamento, informe os dados financeiros e revise o destino antes de enviar.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <CategoryRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Classificação e projeto</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl required className='ui-control'>
                                <InputLabel id='labelCategoria'>Categoria</InputLabel>
                                <Select
                                    labelId='labelCategoria'
                                    label='Categoria'
                                    value={categoria}
                                    onChange={(e) => setCategoria(e.target.value)}
                                >
                                    {reimbursementCategories.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl required className='ui-control'>
                                <InputLabel id='labelProjeto'>Projeto</InputLabel>
                                <Select
                                    labelId='labelProjeto'
                                    label='Projeto'
                                    value={projeto}
                                    onChange={(e) => setProjeto(e.target.value)}
                                >
                                    {projetos.map((item, index) => (
                                        <MenuItem key={getProjetoValue(item) || index} value={getProjetoValue(item)}>
                                            {getProjetoLabel(item)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Dados financeiros</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <TextField
                                required
                                label='Valor'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valor}
                                onChange={(e) => setValor(e.target.value)}
                            />
                            <TextField
                                required
                                label='Nota Fiscal'
                                value={notaFiscal}
                                onChange={(e) => setNotaFiscal(e.target.value)}
                            />
                            <FormControl className='ui-control' error={errorVencimento}>
                                <DayDatePicker
                                    id='dataVencimento'
                                    label='Data Vencimento'
                                    value={dataVencimento}
                                    onChange={setDataVencimento}
                                />
                                {errorVencimento && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <PixRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Destino do pagamento</strong>
                            </Box>
                        </Box>

                        <Box className='reimbursement-pix-strip'>
                            <AccountBalanceWalletRoundedIcon />
                            <TextField
                                required
                                label='Chave Pix'
                                value={chavePix}
                                onChange={(e) => setChavePix(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='ui-rail'>
                        <Box>
                            <PaymentsRoundedIcon />
                            <span>Operação</span>
                            <strong>Reembolso</strong>
                        </Box>
                        <Box>
                            <CalendarMonthRoundedIcon />
                            <span>Vencimento</span>
                            <strong>{dueDateLabel}</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do reembolso</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Categoria</span>
                            <strong>{selectedCategory?.label}</strong>
                        </Box>
                        <Box>
                            <span>Projeto</span>
                            <strong className={!selectedProjectLabel ? 'is-empty' : undefined}>
                                {selectedProjectLabel || 'Não selecionado'}
                            </strong>
                        </Box>
                        <Box>
                            <span>Valor</span>
                            <strong>{valueLabel}</strong>
                        </Box>
                        <Box>
                            <span>Nota Fiscal</span>
                            <strong className={!notaFiscal ? 'is-empty' : undefined}>{notaFiscal || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Vencimento</span>
                            <strong className={!dataVencimento ? 'is-empty' : undefined}>{dueDateLabel}</strong>
                        </Box>
                        <Box>
                            <span>Chave Pix</span>
                            <strong className={!chavePix ? 'is-empty' : undefined}>{chavePix || 'Não informada'}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <RequestQuoteRoundedIcon />
                        <p>
                            O lançamento será enviado com projeto, categoria, valor, vencimento, nota fiscal e chave Pix informados.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/6 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<PaymentsRoundedIcon />}>
                        Lançar Reembolso
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default LancarReembolso;
